/**
 * Tauri bridge for bulk sync commands.
 *
 * TypeScript only triggers actions — Rust does all the work.
 * These are app-level Tauri commands (not plugin commands), so they use
 * `invoke()` directly without a command prefix.
 */
import { isTauri } from '@/tauri/utils';

class BulkSyncBridgeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BulkSyncBridgeError';
  }
}

async function invokeBulkSyncCommand<T>(
  command: string,
  payload?: Record<string, unknown>
): Promise<T> {
  if (!isTauri()) {
    throw new BulkSyncBridgeError(
      'Bulk sync bridge is only available inside the Tauri runtime'
    );
  }

  const { invoke } = await import('@tauri-apps/api/core');
  return invoke<T>(command, payload ?? {});
}

/**
 * Bridge for bulk sync Tauri commands.
 * TypeScript only triggers actions — Rust does all the work.
 */
export const bulkSyncBridge = {
  // --- Bulk Download (BLE Phase) ---

  startDownload: (userId: string, deviceSerial: string) =>
    invokeBulkSyncCommand<void>('bulk_download_start', {
      userId,
      deviceSerial,
    }),

  stopDownload: () => invokeBulkSyncCommand<void>('bulk_download_stop'),

  // --- Session Creation Engine (Network Phase) ---

  startEngine: (userId: string) =>
    invokeBulkSyncCommand<void>('session_engine_start', { userId }),

  /**
   * Pushes the remotely-configured max chunk uploads in flight (per bulk-sync
   * pass) into the engine. Flagsmith-driven; Rust clamps it and applies it on
   * the next task. Fire-and-forget — mirrors `setTranscriptionConfig`.
   */
  setChunkUploadConcurrency: (value: number) =>
    invokeBulkSyncCommand<void>('session_engine_set_chunk_upload_concurrency', {
      value,
    }),

  /**
   * Locks the engine force-refresh gate. Omit → legacy FE `token_needed` handshake.
   *
   * `epoch` is the account epoch captured when Rust asked for this token (the
   * `token_needed` payload, or `accountTransition`'s return value). Rust
   * rejects the write if the account has since switched.
   */
  provideToken: (
    token: string,
    baseUrl: string,
    epoch: number,
    rustAuth0TokenRefresh = false
  ) =>
    invokeBulkSyncCommand<void>('session_engine_provide_token', {
      token,
      baseUrl,
      epoch,
      rustAuth0TokenRefresh,
    }),

  clearToken: () => invokeBulkSyncCommand<void>('session_engine_clear_token'),

  invalidateNoteDefaultsCache: () =>
    invokeBulkSyncCommand<void>('session_engine_invalidate_note_defaults_cache'),

  /**
   * Drains offline-cache `upload_failed` rows (device-independent re-sync) from
   * the local copy. Returns the number of records submitted to the engine.
   */
  retryFailed: (userId: string) =>
    invokeBulkSyncCommand<number>('session_engine_retry_failed', { userId }),

  // --- Recovery state queries ---

  /**
   * Returns the distinct session IDs of live-recording sync_tracking rows
   * that are awaiting bulk-sync recovery (canonical
   * `status = 'session_created'` state). Used by the frontend to disable
   * resume on truly-interrupted Remote sessions. Once bulk sync starts
   * advancing the row past `session_created` the audio is already on the
   * server via the live chunk pipeline, so it's omitted here.
   */
  listInterruptedSessions: (userId: string) =>
    invokeBulkSyncCommand<string[]>('list_interrupted_sessions', { userId }),

  // --- WiFi ---

  setupWifi: () => invokeBulkSyncCommand<void>('wifi_setup'),

  teardownWifi: () => invokeBulkSyncCommand<void>('wifi_teardown'),

  getWifiStatus: () => invokeBulkSyncCommand<{ status: number }>('wifi_status'),

  // --- Auto Bulk Sync ---

  /**
   * `epoch` is the account epoch captured when Rust asked for this identity
   * (the `chronicle:auto_bulk_sync_token_needed` payload, or
   * `accountTransition`'s return value). Rust rejects the write if the
   * account has since switched.
   */
  provideAutoSyncAuth: (
    token: string,
    baseUrl: string,
    userId: string,
    epoch: number,
    deviceSerial?: string | null
  ) =>
    invokeBulkSyncCommand<void>('auto_bulk_sync_provide_auth', {
      token,
      baseUrl,
      userId,
      epoch,
      deviceSerial: deviceSerial ?? null,
    }),

  clearAutoSyncAuth: () => invokeBulkSyncCommand<void>('auto_bulk_sync_clear_auth'),

  // --- Account epoch ---

  /** Current account epoch, without advancing it (initial provisioning). */
  getAccountEpoch: () => invokeBulkSyncCommand<number>('account_epoch_current'),

  /**
   * Advances the account epoch and clears every native credential cache
   * (session engine, auto-session, auto-bulk-sync, note defaults). Returns
   * the new epoch — callers must use it for every subsequent `provide*` call
   * for the newly-current account. Call when the authenticated account
   * changes while still authenticated (A→B switch, no intervening logout),
   * and also when auth becomes absent (logout) — the epoch bump there is
   * what makes a `provide_*` reply still in flight for the account that just
   * logged out get rejected instead of repopulating cleared native state.
   */
  accountTransition: () => invokeBulkSyncCommand<number>('account_transition'),

  /**
   * Advances the account epoch only if it still equals `expectedEpoch` at
   * the moment of an atomic native compare-and-swap; otherwise a no-op that
   * returns the current (already-advanced) epoch. For a caller whose
   * evidence of a real switch is bound to a specific, already-observed
   * epoch and resolves after the fact (a background corroboration lookup)
   * rather than synchronously: an independently-detected
   * transition may have already advanced past `expectedEpoch` by the time
   * this resolves, and bumping again on top of that would be redundant
   * churn that invalidates the real transition's freshly-provisioned work
   * for no reason. A plain "read the epoch, compare in JS, then call
   * `accountTransition`" sequence does NOT close this race -- Tauri
   * `invoke()` gives no ordering guarantee against a concurrent,
   * independently-dispatched `accountTransition` call, so the read can be
   * stale by the time the unconditional bump actually runs. This command
   * performs the check and the bump atomically, natively, under the same
   * lock every other epoch mutation uses.
   */
  accountTransitionIfEpoch: (expectedEpoch: number) =>
    invokeBulkSyncCommand<ConditionalAccountTransitionResult>(
      'account_transition_if_epoch',
      { expectedEpoch }
    ),

  // --- Migration outcome (APP-4647) ---

  /** Reads (and clears) the migration outcome that Rust stashed at startup.
   *  Returns `null` if the slot was empty. Call once on mount; the consume is
   *  destructive so repeat calls won't double-fire analytics. */
  consumeMigrationOutcome: () =>
    invokeBulkSyncCommand<MigrationStateDto | null>(
      'chronicle_consume_migration_outcome'
    ),
};

/** Mirrors `chronicle::storage::MigrationOutcome` (Rust serializes camelCase). */
export interface MigrationOutcome {
  fromVersion: number;
  toVersion: number;
  targetVersion: number;
  errorCode?: string;
  timestamp?: string;
}

/** Mirrors `chronicle::commands::MigrationStateDto`. */
export interface MigrationStateDto {
  previousFailure?: MigrationOutcome;
  current?: MigrationOutcome;
}

/** Mirrors `auth::commands::ConditionalAccountTransitionResult`. */
export interface ConditionalAccountTransitionResult {
  epoch: number;
  bumped: boolean;
}

export { BulkSyncBridgeError };
